import { useEffect, useRef, EffectCallback, DependencyList } from "react";

/**
 * This hook gets called only when the dependencies change but not during initial render.
 *
 * @param effect The `useEffect` callback function.
 * @param deps An array of dependencies.
 *
 * @example
 * ```
 *  useNonInitialEffect(()=>{
 *      alert("Dependency changed!");
 * },[dependency]);
 * ```
 */
export const useNonInitialEffect = (effect: EffectCallback, deps?: DependencyList): void => {
    const initialRender = useRef(true);

    useEffect(() => {
        let effectReturns: (() => void) | void = () => {};

        if (initialRender.current) {
            initialRender.current = false;
        } else {
            effectReturns = effect();
        }

        if (effectReturns && typeof effectReturns === "function") {
            return effectReturns;
        }
        // eslint-disable-next-line
    }, deps);
};
